
import { all, call, put, takeEvery } from "redux-saga/effects"
import { putWait } from "redux-saga-callback"

import client from "@api/client"
import { iriFromIModelOrIRI } from "@api/entityTypeEndpointDefinitions"
import { addNotificationAction } from "@redux/actions/notifications"
import { UNKNOWN_REQUEST_ERROR } from "@redux/common/constants"
import { newSingleEntityUsecaseRequestRunningAction, newSingleEntityUsecaseRequestSuccessAction } from "@redux/common/entityRequest/actions"
import { usecaseKeyForLoadCollection } from "@redux/common/react-hooks/useEntityCollection"
import { EntityType } from "@redux/common/reduxTypes"
import { showErrorsInTestEnvironment } from "@redux/common/sagaErrorHelpers"
import { newLoadCollectionAction } from "@redux/common/scopedObject/actions"
import { prefixedKey } from "@services/i18n/i18n"

import { DocumentUsecase, ICreateDocumentPdfAction, IMoveChapterAction } from "./definitions"

export function* documentWatcherSaga(): any {
  yield all([
    takeEvery(DocumentUsecase.MoveChapter, moveChapterSaga),
    takeEvery(DocumentUsecase.CreatePDF, createDocumentPdfSaga)
  ])
}

/**
 * Saga for moving a chapter within the document.
 * Saga reloads the chapters to update the new sortOrders.
 */
function* moveChapterSaga(action: IMoveChapterAction): Generator<any, void, any> {

  // NOTE: 2 requests are running, both use the same usecaseKey: one for single requests,
  // the other indirectly for a collection request, but the request reducer stores the
  // request data within different AppState branches
  try {
    yield put(newSingleEntityUsecaseRequestRunningAction(EntityType.Chapter, action.usecaseKey))

    // Returns response code 204 but no content on success.
    yield call(client.moveChapter, action.chapterToBeMoved, action.subsequentChapter)

    // Reloading the chapters, because the sortOrder has changed
    const parentIri = iriFromIModelOrIRI(action.chapterToBeMoved.document)
    // Using putWait instead of put, because when the request has the state loaded, the chapters should be loaded,
    // needed for the 'DocumentComponent' to show only one Spinner. Otherwise 2 Spinner would be visible, one
    // for the chapterRequest and one for the chapterOrderRequest.
    yield putWait(newLoadCollectionAction(
      EntityType.Chapter,
      null,
      usecaseKeyForLoadCollection(null, null, parentIri),
      parentIri,
      true /* loadAll */
    ))

    yield put(newSingleEntityUsecaseRequestSuccessAction(EntityType.Chapter, action.usecaseKey, null))
  } catch (err) {

    const errorMessage = err instanceof Error ? err.message : UNKNOWN_REQUEST_ERROR
    showErrorsInTestEnvironment("moveChapterSaga", errorMessage, action, err)

    // signalize that this use case has an error
    yield put(newSingleEntityUsecaseRequestRunningAction(EntityType.Chapter, action.usecaseKey, errorMessage))

    return null
  }
}

/**
 * saga to trigger the creation of an document-pdf
 */
function* createDocumentPdfSaga(action: ICreateDocumentPdfAction): Generator<any, void, any> {
  try {
    yield call(client.createDocumentPdf, action.document)
    yield put(addNotificationAction(prefixedKey("default", "pdfFile.pdfCreationTriggered"), "success"))
  } catch (err) {
    if (err instanceof Error) {
      yield put(addNotificationAction(err.message, "error"))
      const errorMessage = err instanceof Error ? err.message : UNKNOWN_REQUEST_ERROR
      showErrorsInTestEnvironment("createDocumentPdfSaga", errorMessage, action, err)
    } else {
      // @todo
    }
  }
}